/**
 * ApprovalTasksInbox — V22.9.8 Backlog #1
 *
 * Dashboard inbox card showing pending approval tasks where the current user
 * is the named Approver. Approving / Rejecting writes to /approval-tasks/{id}/decide
 * which emits an `approval_task.{approved|rejected}` audit row.
 *
 * Only renders when the user has 1+ pending tasks — invisible otherwise.
 */
import { useEffect, useState } from "react";
import { formatTier } from "../lib/tierLabel";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { CheckCircle2, XCircle, ClipboardCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ApprovalTasksInbox() {
  const { api } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/asset-profiles/approval-tasks/mine");
      setTasks(res.data?.tasks || []);
    } catch { /* silent — non-critical */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const decide = async (id, decision) => {
    setBusyId(id);
    try {
      await api.post(`/asset-profiles/approval-tasks/${id}/decide`, { decision });
      toast.success(`Task ${decision}`);
      setTasks((prev) => prev.filter((t) => t.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decision failed");
    } finally {
      setBusyId(null);
    }
  };

  if (loading || tasks.length === 0) return null;

  return (
    <div
      className="mb-4 rounded-lg border border-violet-300 bg-violet-50 p-3"
      data-testid="approval-tasks-inbox"
    >
      <div className="flex items-center gap-2 mb-2">
        <ClipboardCheck className="w-4 h-4 text-violet-700" />
        <p className="text-sm font-semibold text-violet-900">
          {tasks.length} pending approval{tasks.length > 1 ? "s" : ""}
        </p>
        <Badge variant="outline" className="text-[9px] bg-white border-violet-200 text-violet-700">
          ISO 22301 §8.2.2 · RACI
        </Badge>
      </div>
      <div className="space-y-1.5">
        {tasks.slice(0, 5).map((t) => (
          <div
            key={t.id}
            className="flex items-center justify-between gap-3 bg-white border border-violet-100 rounded p-2"
            data-testid={`approval-task-${t.id}`}
          >
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-800 truncate">
                {t.summary || `Approve criticality for ${t.asset_name}`}
              </p>
              <p className="text-[10px] text-slate-500">
                <span className="capitalize">{(t.asset_type || "").replace(/_/g, " ")}</span>
                {t.tier ? ` · ${formatTier(t.tier).toUpperCase()}` : ""}
                {t.created_at ? ` · ${new Date(t.created_at).toLocaleDateString()}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={() => decide(t.id, "approved")}
                disabled={busyId === t.id}
                data-testid={`approval-task-${t.id}-approve`}
              >
                {busyId === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                Approve
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                onClick={() => decide(t.id, "rejected")}
                disabled={busyId === t.id}
                data-testid={`approval-task-${t.id}-reject`}
              >
                <XCircle className="w-3 h-3" />
                Reject
              </Button>
            </div>
          </div>
        ))}
        {tasks.length > 5 && (
          <p className="text-[10px] text-violet-600 text-center">
            +{tasks.length - 5} more — handled in order
          </p>
        )}
      </div>
    </div>
  );
}
